import * as React from "react";
import { CartesianGrid, XAxis, YAxis } from "recharts";

import { cn } from "../../utils";

/**
 * `<CartesianGrid>` configured to the Lens default: dashed, border-colored,
 * with optional axis hiding via `vertical` / `horizontal` opt-outs.
 *
 * Replaces nine copies of the same `<CartesianGrid strokeDasharray="3 3"
 * stroke="var(--border)">` config across Lens charts.
 */
export function ChartGrid({
  vertical = true,
  horizontal = true,
}: {
  vertical?: boolean;
  horizontal?: boolean;
}) {
  return (
    <CartesianGrid
      strokeDasharray="3 3"
      stroke="var(--border)"
      vertical={vertical}
      horizontal={horizontal}
    />
  );
}

type ChartAxisCommonProps = {
  dataKey?: string;
  // Recharts types this as `(value: any, index: number) => string`. Match it.
  tickFormatter?: (value: any, index: number) => string; // eslint-disable-line @typescript-eslint/no-explicit-any
  hide?: boolean;
  width?: number;
  type?: "number" | "category";
  interval?: number | "preserveStartEnd" | "preserveStart" | "preserveEnd";
  minTickGap?: number;
  label?: React.ComponentProps<typeof XAxis>["label"];
};

/**
 * `<XAxis>` with the Lens default tick styling: muted-foreground fill, no
 * axis line, no tick line. Use `ChartYAxis` for the Y-axis equivalent.
 */
export function ChartXAxis(props: ChartAxisCommonProps) {
  return (
    <XAxis
      {...props}
      tick={{ fill: "var(--muted-foreground)" }}
      tickLine={false}
      axisLine={false}
    />
  );
}

/** Y-axis variant. Same defaults as `ChartXAxis`. */
export function ChartYAxis(props: ChartAxisCommonProps) {
  return (
    <YAxis
      {...props}
      tick={{ fill: "var(--muted-foreground)" }}
      tickLine={false}
      axisLine={false}
    />
  );
}

interface ChartTooltipBoxProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Standard wrapper for Recharts `<Tooltip content={...}>` custom renderers.
 * Replaces four near-identical `<div className="bg-card surface-edge rounded
 * px-3 py-2 type-body">` clones across Lens charts.
 *
 * Usage:
 *
 *   <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
 *     <ChartTooltipBox>
 *       <p className="text-foreground mb-1">{formatDateFull(label)}</p>
 *       …
 *     </ChartTooltipBox>
 *   ) : null} />
 */
export function ChartTooltipBox({ className, children }: ChartTooltipBoxProps) {
  return (
    <div
      className={cn(
        "bg-card surface-edge rounded-lg px-3 py-2 type-body",
        className,
      )}
    >
      {children}
    </div>
  );
}

export { CHART_TOOLTIP_STYLE, CHART_COLORS, MODEL_COLOR_BY_FAMILY, colorForModel, shortModelName } from "./chart-config";
